/**
 * The agents cluster of the work record — who represents this work, and the
 * modal that names somebody — wired to `fetchWorkAgents`, `checkEligibility`,
 * `assignWorkAgent` and `endAgentMandate`. Composed by `useWorkController.ts`,
 * which hands it the record's `busy` and `error` setters rather than letting
 * it own a second pair.
 *
 * It owns the mandate list, whether the modal is open, and the draft kept
 * separately from that flag so closing is not discarding. `work.model.ts`
 * decides what the panel and the modal show; nothing here tests whether a
 * principal is an agent — that verdict arrives on the response.
 */

import type { WorkAgent } from "@ondo/shared";
import { useCallback, useEffect, useState } from "react";

import {
  assignWorkAgent,
  checkEligibility,
  endAgentMandate,
  fetchWorkAgents,
  renewAgentMandate,
  setAgentMandateScope,
} from "../lib/api.ts";
import { WORK_AGENT_COPY } from "./work.constants.ts";
import { describeError, isOndoIdShape } from "./work.model.ts";
import type { AgentTermsDraft, AssignAgentView, ScopeDraft } from "./work.model.ts";
import { EMPTY_AGENT_TERMS, toAgentTerms, toScopeRequest } from "./work.model.ts";

/** What the assign modal does, as the one bundle the screen hands it. */
export interface AgentHandlers {
  onSetOndoId: (value: string) => void;
  onSetNote: (value: string) => void;
  /** The optional term, as `<input type="date">` gives it: 'YYYY-MM-DD' or ''. */
  onSetExpiresOn: (value: string) => void;
  /** The owner's one delegation permission. */
  onSetMayDelegate: (value: boolean) => void;
  /**
   * OND-83's recipient choice, as the field holds it: an organisation's ONDO id
   * or ''. Empty is the named agent alone, which is the default.
   */
  onSetOrganisationOndoId: (value: string) => void;
  /** OND-82's permission. Unticked means the appointment may not consign. */
  onSetMayConsign: (value: boolean) => void;
  /**
   * Replaces one appointment's recorded scope.
   *
   * THE WHOLE SCOPE, so the draft the row hands over is the scope AFTER the
   * call — an empty organisation removes one. `scopeDraftOf` seeds that draft
   * from the appointment for exactly this reason.
   */
  onSetScope: (mandateId: string, draft: ScopeDraft) => void;
  /** One field of the agreed terms at a time, so the draft stays one object. */
  onSetTerm: <K extends keyof AgentTermsDraft>(key: K, value: AgentTermsDraft[K]) => void;
  /**
   * Extends one appointment's term. Either side alone may, so there is no
   * proposal state and nothing to await.
   */
  onRenew: (mandateId: string, expiresOn: string) => void;
  /**
   * Asks the server whether this id can be assigned, on blur.
   *
   * A COURTESY AND NOT A GATE. `canSubmit` does not wait for it — see
   * `toAssignAgentView` — because a button held dead by a request that never
   * returned is worse than one that submits and is refused by the write. Both
   * paths end at the same function in SQL, so they cannot disagree.
   */
  onCheck: () => void;
  onSubmit: () => void;
  onEnd: (mandateId: string) => void;
  /** Escape, the backdrop and ✕. Keeps what was typed. */
  onClose: () => void;
  /** Cancel alone. Discards it. */
  onCancel: () => void;
}

/** The agents cluster's own state, as `resolveWorkRecordView` reads it. */
export interface WorkAgentsState {
  agents: WorkAgent[] | null;
  agentOpen: boolean;
  agentId: string;
  agentNote: string;
  agentExpiresOn: string;
  agentMayDelegate: boolean;
  agentOrganisationId: string;
  agentMayConsign: boolean;
  agentTerms: AgentTermsDraft;
  agentCheck: AssignAgentView["check"];
}

/**
 * Who represents this work, and the modal that names one — composed by
 * `useWorkController`.
 *
 * Takes the record's `busy` and `error` setters rather than owning a pair,
 * because both render in one place on the screen and two spinners would be a
 * lie about how much is happening. See `runAgents`.
 */
export function useWorkAgents(input: {
  workId: string;
  setBusy: (busy: boolean) => void;
  setError: (error: string | null) => void;
}): WorkAgentsState & { assignAgent: () => void; handlers: AgentHandlers } {
  const { workId, setBusy, setError } = input;

  /**
   * Who represents this work, and the modal that names one.
   *
   * `null` is "not loaded yet" and `[]` is "nobody" — the two render
   * differently, so they cannot share a value. The draft is three separate
   * pieces rather than one object for the reason the history modal keeps its
   * draft outside `dialogOpen`: closing is not discarding, and a collector who
   * shuts the modal to check an id in their email should find it as they left
   * it.
   */
  const [agents, setAgents] = useState<WorkAgent[] | null>(null);
  const [agentOpen, setAgentOpen] = useState(false);
  const [agentId, setAgentId] = useState("");
  const [agentNote, setAgentNote] = useState("");
  // OPEN-ENDED AND UNTERMED AND UNDELEGATED, which is what every appointment
  // made before these fields existed is. A draft that opened with a date in it
  // would make the exception the default.
  const [agentExpiresOn, setAgentExpiresOn] = useState("");
  const [agentMayDelegate, setAgentMayDelegate] = useState(false);
  // AGENT-ONLY AND PERMITTED NOTHING EXTRA, which is the narrower of OND-83's
  // two choices and OND-82's default. A draft that opened with the agent's own
  // firm in it would make the wider choice the one a collector got by not
  // reading the field — and the firm is NAMED, never derived, so there is
  // nothing to pre-fill it from anyway.
  const [agentOrganisationId, setAgentOrganisationId] = useState("");
  const [agentMayConsign, setAgentMayConsign] = useState(false);
  const [agentTerms, setAgentTerms] = useState<AgentTermsDraft>(EMPTY_AGENT_TERMS);
  const [agentCheck, setAgentCheck] = useState<AssignAgentView["check"]>(null);

  /**
   * Who represents this work.
   *
   * Read separately from the chain rather than folded into `loadProvenance`,
   * because they fail independently: `ondo.work_agents()` returns nothing to a
   * caller who may not see the mandates, and that must not take the provenance
   * list down with it. A failure leaves `agents` at `[]` — the panel then reads
   * "no agent assigned", which is what a caller who cannot see them should see
   * anyway.
   */
  const loadAgents = useCallback(async () => {
    try {
      return await fetchWorkAgents(workId);
    } catch {
      return [];
    }
  }, [workId]);

  useEffect(() => {
    let active = true;
    void loadAgents().then((found) => {
      if (active) setAgents(found);
    });
    return () => {
      active = false;
    };
  }, [loadAgents]);

  /**
   * A write against the mandate list, then a re-read.
   *
   * Its own helper rather than `run`, which re-reads the CHAIN — a mandate does
   * not renumber a provenance entry and there is nothing about the two to keep
   * in step. It shares `busy` and `error` with the rest of the screen, because
   * both render in one place and two spinners would be a lie about how much is
   * happening.
   */
  const runAgents = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      setAgents(await loadAgents());
    } catch (cause: unknown) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  return {
    agents,
    agentOpen,
    agentId,
    agentNote,
    agentExpiresOn,
    agentMayDelegate,
    agentOrganisationId,
    agentMayConsign,
    agentTerms,
    agentCheck,
    assignAgent: () => {
      setError(null);
      setAgentOpen(true);
    },
    handlers: {
      onSetOndoId: (value) => {
        setAgentId(value);
        // The verdict was about the id that has just changed, so it is dropped
        // rather than left to describe a different one. A stale "Eleanor Laing"
        // under a half-retyped id is the worst state this modal can be in.
        setAgentCheck(null);
      },
      onSetNote: setAgentNote,
      onCheck: () => {
        const typed = agentId.trim();
        // Nothing to ask about, and a malformed id is refused by shape before
        // it reaches the server — asking would spend a request to be told what
        // the regex already knows, on a route that has to be rate-limited.
        if (typed.length === 0 || !isOndoIdShape(typed)) return;

        setAgentCheck({ state: "checking" });
        void checkEligibility({
          ondoId: typed,
          capacity: "agent",
          subject: { kind: "work", id: workId },
        })
          .then((answer) => {
            setAgentCheck(
              answer.verdict === "eligible" && answer.principal
                ? { state: "eligible", displayName: answer.principal.displayName }
                : {
                    state: "refused",
                    // The server's sentence, never one written here: it is the
                    // same string the write raises.
                    message: answer.message ?? WORK_AGENT_COPY.malformed,
                  },
            );
          })
          // A failed CHECK is silent. It is a courtesy, and reporting that the
          // courtesy broke would put an error in front of somebody who has done
          // nothing wrong and can still press Assign.
          .catch(() => setAgentCheck(null));
      },
      onSubmit: () => {
        void runAgents(async () => {
          await assignWorkAgent(workId, {
            ondoId: agentId.trim(),
            note: agentNote.trim() === "" ? null : agentNote.trim(),
            mayDelegate: agentMayDelegate,
            // A DATE INPUT GIVES A DAY AND THE COLUMN WANTS AN INSTANT, so the
            // day is read as ending at the END of itself rather than at its
            // start. A collector who types the 31st of December means the
            // appointment to run through that day, and `T00:00:00Z` would have
            // ended it the night before — a whole day early, on the one field
            // where being a day out is the entire point of the feature.
            expiresAt:
              agentExpiresOn === "" ? null : `${agentExpiresOn}T23:59:59.000Z`,
            // `?? null` RATHER THAN THE BARE CALL, because
            // `exactOptionalPropertyTypes` is on: an optional property may hold
            // its own type or be absent, and `undefined` is neither.
            // `toAgentTerms` already answers null when nothing was typed, so
            // this guards the signature rather than changing any value.
            terms: toAgentTerms(agentTerms) ?? null,
            // OND-83 and OND-82. Trimmed and upper-cased here so the ISO-ish
            // shape the server validates never sees 'ondo-5592' from this
            // screen; empty becomes null, because on the wire null is the
            // "agent alone" answer and '' is a malformed id.
            organisationOndoId:
              agentOrganisationId.trim() === ""
                ? null
                : agentOrganisationId.trim().toUpperCase(),
            mayConsign: agentMayConsign,
          });
          // Cleared only on success. A refused attempt keeps the id on screen,
          // because the collector needs to see what they typed to correct it.
          setAgentOpen(false);
          setAgentId("");
          setAgentNote("");
          setAgentExpiresOn("");
          setAgentMayDelegate(false);
          setAgentOrganisationId("");
          setAgentMayConsign(false);
          setAgentTerms(EMPTY_AGENT_TERMS);
          setAgentCheck(null);
        });
      },
      onSetExpiresOn: setAgentExpiresOn,
      onSetMayDelegate: setAgentMayDelegate,
      onSetOrganisationOndoId: setAgentOrganisationId,
      onSetMayConsign: setAgentMayConsign,
      onSetScope: (mandateId, draft) => {
        void runAgents(() =>
          setAgentMandateScope(workId, mandateId, toScopeRequest(draft)),
        );
      },
      onSetTerm: (key, value) => {
        setAgentTerms((draft) => ({ ...draft, [key]: value }));
      },
      onEnd: (mandateId) => {
        void runAgents(() => endAgentMandate(workId, mandateId));
      },
      onRenew: (mandateId, expiresOn) => {
        if (expiresOn === "") return;
        // THE SAME END-OF-DAY READING AS THE APPOINTMENT, and it matters more
        // here: the database refuses a renewal that does not EXTEND the term, so
        // a date read as midnight could be refused for being the same day the
        // term already runs to.
        void runAgents(() =>
          renewAgentMandate(workId, mandateId, {
            expiresAt: `${expiresOn}T23:59:59.000Z`,
          }),
        );
      },
      onClose: () => setAgentOpen(false),
      onCancel: () => {
        setAgentOpen(false);
        setAgentId("");
        setAgentNote("");
        // THE SCOPE IS DISCARDED WITH THE REST, and it has to be: a permission
        // left ticked from an abandoned draft would be granted by the next
        // appointment made from this dialog, which is the one mistake in this
        // feature that is invisible afterwards.
        setAgentOrganisationId("");
        setAgentMayConsign(false);
        setAgentCheck(null);
      },
    },
  };
}
